import type { NavConfig, NavItem } from './types/nav'
import { buildMenuPath, normalizePrefix } from './menuUrl'

/** 开发者社区（固定外链） */
export const FORUM_URL = 'https://dev.comake.online/home?pinned=1&zone=0'

const LABELS = {
  zh: {
    forum: '开发者社区',
    news: '资讯',
    activityCenter: '活动中心',
    caseStudies: '应用案例',
    productNews: '产品资讯',
    product: '产品选型',
    banka: '开发板',
    peijian: '配件',
    chip: '芯片选型',
    support: '技术资料',
    sdk: 'SDK 下载',
    docs: '文档中心',
    training: '技术培训',
    chipCat: '芯片',
    simulation: '端侧 AI 云仿真',
    shop: '商店',
    about: '关于Comake'
  },
  en: {
    forum: 'Developer Community',
    news: 'News',
    activityCenter: 'Events',
    caseStudies: 'Application Cases',
    productNews: 'Product News',
    product: 'Products',
    banka: 'Dev Boards',
    peijian: 'Accessories',
    chip: 'Chip Selection',
    support: 'Support',
    sdk: 'SDK Downloads',
    docs: 'Documentation',
    training: 'Training',
    chipCat: 'Chip',
    simulation: 'On-device AI Cloud Simulator',
    shop: 'Store',
    about: 'About Comake'
  }
} as const

type Lang = keyof typeof LABELS

interface MenuNode {
  path?: string
  absoluteKey?: string
  labelKey: keyof typeof LABELS.zh
  groupOnly?: boolean
  children?: MenuNode[]
}

/** 与 SSG `useNavMenu` 同构的站点默认菜单（路径段，由 prefix + lang 拼完整链接） */
const MENU_TREE: MenuNode[] = [
  { absoluteKey: FORUM_URL, labelKey: 'forum' },
  {
    path: '/product',
    labelKey: 'product',
    children: [
      { path: '/product/banka', labelKey: 'banka' },
      { path: '/product/peijian', labelKey: 'peijian' },
      { path: '/product/chip', labelKey: 'chip' }
    ]
  },
  {
    path: '/support',
    labelKey: 'support',
    children: [
      {
        path: '/support/group/banka',
        labelKey: 'banka',
        groupOnly: true,
        children: [
          { path: '/support/sdk', labelKey: 'sdk' },
          { path: '/support/docs', labelKey: 'docs' },
          { path: '/support/training', labelKey: 'training' }
        ]
      },
      {
        path: '/support/group/chip',
        labelKey: 'chipCat',
        groupOnly: true,
        children: [
          { path: '/support/chip-sdk', labelKey: 'sdk' },
          { path: '/support/chip-docs', labelKey: 'docs' },
          { path: '/support/training', labelKey: 'training' }
        ]
      }
    ]
  },
  { path: '/simulation', labelKey: 'simulation' },
  { path: '/shop', labelKey: 'shop' },
  {
    path: '/news',
    labelKey: 'news',
    children: [
      { path: '/news/activities', labelKey: 'activityCenter' },
      { path: '/news/cases', labelKey: 'caseStudies' },
      { path: '/news/product-news', labelKey: 'productNews' }
    ]
  },
  { path: '/about', labelKey: 'about' }
]

function buildNode(
  node: MenuNode,
  labelLang: Lang,
  pathLang: Lang,
  prefix: string
): NavItem {
  const labels = LABELS[labelLang]
  const key = node.absoluteKey ?? buildMenuPath(node.path!, pathLang, prefix)
  const item: NavItem = {
    key,
    label: labels[node.labelKey]
  }
  if (node.groupOnly) item.groupOnly = true
  if (node.children?.length) {
    item.children = node.children.map((c) => buildNode(c, labelLang, pathLang, prefix))
  }
  return item
}

/** 根据语言与站点 prefix 生成默认导航配置；pathLang 可单独指定以仅切换文案不改链接 */
export function buildDefaultMenuConfig(
  lang: string,
  prefix?: string,
  pathLang?: string
): NavConfig {
  const labelCode: Lang = lang === 'en' ? 'en' : 'zh'
  const pathCode: Lang = pathLang === 'en' ? 'en' : 'zh'
  const base = normalizePrefix(prefix)
  return MENU_TREE.map((n) => buildNode(n, labelCode, pathCode, base))
}
